import { useState } from "react";
import { t } from "@lingui/core/macro";
import { Button, Chip, Switch } from "@heroui/react";
import { Ban, ChevronRight, Clock } from "lucide-react";
import { LimeCollapse } from "@/Components/NextBase/LimeCollapse";
import { channelIcons } from "@/constants/channelIcons";
import { cn } from "@/utils";
import type {
  GetNotificationChannels,
  GetNotificationSettings,
} from "@/server-types";
import {
  getActiveChannelsForVariant,
  pickAppointmentVariant,
} from "@/utils/notification-variant";
import {
  getNotificationFailureExplanation,
  NotificationLogDetailModal,
  statusIcons,
} from "./NotificationLog";
import {
  getNotificationOffsetLabel,
  getNotificationSettingLabel,
  renderChannelIcons,
  type NotificationSettingToggle,
} from "./NotificationSettingsSwitchList";
import {
  type BatchConfirmation,
  buildNotificationRowGroups,
  splitOffDisabledInSettings,
  type NotificationLog,
  type NotificationRow,
  type NotificationRowGroup,
  type NotificationRowState,
  type NotificationScheduleEntry,
  type WaitingEvent,
} from "./appointmentNotificationRows";
import {
  capitalizeFirst,
  formatAppointmentTime,
  formatSendTime,
  wontSendReasonText,
} from "./notificationSendTime";

type NotificationSettingDetails =
  GetNotificationSettings["response"]["settings"][number];

function failureReason(log: NotificationLog): string | null {
  const statusMessage = log.status_message ?? "";

  if (statusMessage === "MONTHLY_LIMIT_EXCEEDED") {
    return t`Presežena mesečna omejitev`;
  }
  if (statusMessage === "NO_DELIVERABLE_CHANNEL") {
    return t`Stranka nima kontaktnih podatkov`;
  }
  if (statusMessage === "INVALID_NUMBER OR CONTENT") {
    return t`Neveljavna številka ali vsebina`;
  }
  if (statusMessage.startsWith("COUNTRY_RESTRICTED")) {
    return t`Pošiljanje v to državo ni omogočeno`;
  }
  return null;
}

function waitingLabel(event: WaitingEvent): string {
  switch (event) {
    case "rescheduled":
      return t`Čaka na prestavitev`;
    case "canceled":
      return t`Čaka na odpoved`;
    case "noShow":
      return t`Čaka na neprihod`;
  }
}

function rowReason(state: NotificationRowState, now: Date): string | null {
  if (state.kind === "failed") return failureReason(state.log);
  if (state.kind === "wontSend") return wontSendReasonText({ ...state, now });
  if (state.kind === "skippedInvalid") {
    return t`Stranka je izbrisana ali nima nastavljenega jezika.`;
  }
  if (state.kind === "sentWithBatch") {
    const confirmedAt = formatAppointmentTime(
      state.confirmedAppointmentStartTime,
    );
    return t`Stranka je bila dodana na več terminov hkrati. Potrditev je bila poslana enkrat, za termin ${confirmedAt}.`;
  }
  return null;
}

const MUTED_STATES = new Set<NotificationRowState["kind"]>([
  "canceled",
  "disabledForAppointment",
  "disabledInSettings",
  "noChannel",
  "notScheduled",
  "notSent",
  "optedOut",
  "skippedInvalid",
]);

export function AppointmentNotificationsSection({
  settings,
  notificationLog,
  notificationSchedule,
  disabledNotificationSettingIds,
  batchConfirmation = null,
  toggles,
  onToggle,
  selectedServices,
  locationId,
  notificationChannels,
  isNewAppointment = false,
}: {
  settings: NotificationSettingDetails[] | undefined;
  notificationLog: NotificationLog[];
  notificationSchedule: NotificationScheduleEntry[];
  disabledNotificationSettingIds: number[];
  batchConfirmation?: BatchConfirmation;
  toggles: NotificationSettingToggle[];
  onToggle: (settingId: number, enabled: boolean) => void;
  selectedServices: { serviceId: number | string }[];
  locationId: number | null;
  notificationChannels: GetNotificationChannels["response"] | undefined;
  isNewAppointment?: boolean;
}) {
  const serviceIds: (number | null)[] =
    selectedServices.length > 0
      ? selectedServices.map((service) =>
          service?.serviceId != null ? Number(service.serviceId) : null,
        )
      : [null];

  const now = new Date();
  const allGroups = buildNotificationRowGroups({
    settings,
    notificationLog,
    notificationSchedule,
    disabledNotificationSettingIds,
    batchConfirmation,
    toggles,
    isNewAppointment,
    now,
    labelForSetting: getNotificationSettingLabel,
    detailForSetting: getNotificationOffsetLabel,
    resolveChannelCount: (setting) => {
      const variants = setting.variants ?? [];
      if (variants.length === 0) return notificationChannels ? 1 : 0;

      return new Set(
        serviceIds.flatMap((serviceId) =>
          getActiveChannelsForVariant(
            pickAppointmentVariant(variants, {
              serviceId,
              locationId: locationId ?? null,
            }),
          ).map((channel) => channel.channel),
        ),
      ).size;
    },
  });

  const { groups, disabledGroups } = splitOffDisabledInSettings(allGroups);

  function renderGroup(group: NotificationRowGroup) {
    return (
      <div key={group.label} className="flex flex-col">
        <p className="pt-2 text-default-500 text-xs">{group.label}</p>
        {group.rows.map((row) => (
          <NotificationStateRow
            key={row.key}
            row={row}
            now={now}
            icons={rowIcons({
              row,
              selectedServices,
              locationId,
              notificationChannels,
            })}
            onToggle={onToggle}
          />
        ))}
      </div>
    );
  }

  if (groups.length === 0 && disabledGroups.length === 0) {
    return (
      <p className="text-default-400 text-sm">
        {t`Za ta termin ni nastavljenih obvestil.`}
      </p>
    );
  }

  return (
    <div className="flex flex-col">
      {groups.map(renderGroup)}
      {disabledGroups.length > 0 ? (
        <DisabledInSettingsList groups={disabledGroups}>
          {disabledGroups.map(renderGroup)}
        </DisabledInSettingsList>
      ) : null}
    </div>
  );
}

function DisabledInSettingsList({
  groups,
  children,
}: {
  groups: NotificationRowGroup[];
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const count = groups.reduce((sum, group) => sum + group.rows.length, 0);

  return (
    <div className="flex flex-col pt-2">
      <Button
        size="sm"
        variant="light"
        className="h-7 justify-start gap-1 px-1 text-default-500 text-xs"
        aria-expanded={isOpen}
        data-identifier="notifications-disabled-in-settings"
        startContent={
          <ChevronRight
            className={cn(
              "size-3.5 transition-transform motion-reduce:transition-none",
              {
                "rotate-90": isOpen,
              },
            )}
          />
        }
        onPress={() => setIsOpen((open) => !open)}
      >
        {t`Izklopljeno v nastavitvah (${count})`}
      </Button>
      <LimeCollapse isOpen={isOpen}>{children}</LimeCollapse>
    </div>
  );
}

function rowIcons({
  row,
  selectedServices,
  locationId,
  notificationChannels,
}: {
  row: NotificationRow;
  selectedServices: { serviceId: number | string }[];
  locationId: number | null;
  notificationChannels: GetNotificationChannels["response"] | undefined;
}): React.ReactNode {
  if (row.setting) {
    return renderChannelIcons(
      row.setting,
      selectedServices,
      locationId,
      notificationChannels,
    );
  }

  if (row.state.kind === "sent" || row.state.kind === "failed") {
    return channelIcons[
      row.state.log.notificationType || row.state.log.originalType
    ];
  }

  return null;
}

function NotificationStateRow({
  row,
  now,
  icons,
  onToggle,
}: {
  row: NotificationRow;
  now: Date;
  icons: React.ReactNode;
  onToggle: (settingId: number, enabled: boolean) => void;
}) {
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const log =
    row.state.kind === "sent" || row.state.kind === "failed"
      ? row.state.log
      : null;
  const isMuted = MUTED_STATES.has(row.state.kind);
  const reason = rowReason(row.state, now);
  const { toggle } = row;

  const content = (
    <div className="flex w-full items-center justify-between gap-3 py-1">
      <div className="flex min-w-0 items-center gap-2">
        <span
          aria-hidden="true"
          className={cn("flex shrink-0 items-center [&_svg]:size-5", {
            "opacity-40": isMuted,
          })}
        >
          {icons}
        </span>
        <span className="flex min-w-0 flex-col">
          <span
            className={cn("truncate text-sm leading-5", {
              "text-default-500": isMuted,
            })}
          >
            {row.label}
            {row.detail ? (
              <span className="text-default-400"> · {row.detail}</span>
            ) : null}
          </span>
          {reason ? (
            <span className="text-default-500 text-xs leading-4">{reason}</span>
          ) : null}
        </span>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <NotificationStateLabel state={row.state} now={now} />
        {toggle ? (
          <Switch
            size="sm"
            aria-label={row.label}
            classNames={{ base: "m-0", wrapper: "m-0" }}
            isSelected={toggle.enabled}
            onValueChange={(enabled) => onToggle(toggle.settingId, enabled)}
          />
        ) : null}
      </div>
    </div>
  );

  if (!log) {
    return content;
  }

  return (
    <>
      <button
        type="button"
        className="cursor-pointer rounded-md text-left hover:bg-default-100"
        onClick={() => setIsDetailOpen(true)}
        title={
          row.state.kind === "failed"
            ? (getNotificationFailureExplanation({
                status: log.status,
                status_message: log.status_message ?? null,
                message_content: log.message_content,
                requestId: null,
              }) ?? undefined)
            : undefined
        }
      >
        {content}
      </button>

      {isDetailOpen ? (
        <NotificationLogDetailModal
          isOpen={isDetailOpen}
          onClose={() => setIsDetailOpen(false)}
          title={`${log.notificationType || log.originalType} - ${log.timeSent}`}
          notificationLogId={log.notificationLogId}
        />
      ) : null}
    </>
  );
}

function NotificationStateLabel({
  state,
  now,
}: {
  state: NotificationRowState;
  now: Date;
}) {
  switch (state.kind) {
    case "willSend":
      return null;
    case "sent":
      return (
        <span className="flex items-center gap-1 text-default-500 text-xs">
          {state.log.timeSent}
          {statusIcons(
            state.log.deliveryStatus,
            state.log.notificationType || state.log.originalType,
          )}
        </span>
      );
    case "failed":
      return (
        <Chip
          size="sm"
          variant="flat"
          color={
            state.log.status_message === "MONTHLY_LIMIT_EXCEEDED"
              ? "warning"
              : "danger"
          }
        >
          {t`Ni poslano`}
        </Chip>
      );
    case "scheduled":
      return (
        <Chip
          size="sm"
          variant="flat"
          color="primary"
          startContent={<Clock className="ml-1 size-3" />}
        >
          {capitalizeFirst(formatSendTime(state.sendAt, now))}
        </Chip>
      );
    case "sending":
      return (
        <span className="text-default-500 text-xs">{t`Pošiljanje …`}</span>
      );
    case "wontSend":
      return (
        <Chip
          size="sm"
          variant="bordered"
          classNames={{ base: "border-small", content: "text-default-500" }}
          startContent={<Ban className="ml-1 size-3 text-default-400" />}
        >
          {t`Ne bo poslano`}
        </Chip>
      );
    case "canceled":
      return <span className="text-default-400 text-xs">{t`Preklicano`}</span>;
    case "optedOut":
      return (
        <span className="text-default-400 text-xs">{t`Stranka odjavljena`}</span>
      );
    case "skippedInvalid":
      return <span className="text-default-400 text-xs">{t`Preskočeno`}</span>;
    case "scheduleError":
      return (
        <Chip size="sm" variant="flat" color="danger">
          {t`Napaka pri pošiljanju`}
        </Chip>
      );
    case "waitingForEvent":
      return (
        <span className="text-default-500 text-xs">
          {waitingLabel(state.event)}
        </span>
      );
    case "disabledForAppointment":
      return (
        <span className="text-default-400 text-xs">{t`Izklopljeno za ta termin`}</span>
      );
    case "disabledInSettings":
      return null;
    case "noChannel":
      return (
        <span className="text-default-400 text-xs">{t`Ni kanala za to storitev`}</span>
      );
    case "notScheduled":
      return (
        <span className="text-default-400 text-xs">{t`Ni načrtovano`}</span>
      );
    case "sentWithBatch":
      return (
        <span className="text-default-500 text-xs">{t`Poslano pri drugem terminu`}</span>
      );
    case "notSent":
      return <span className="text-default-400 text-xs">{t`Ni poslano`}</span>;
  }
}
